import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Shield, Zap } from "lucide-react";

/**
 * QualificationPopup — bandeau bas "Vous souhaitez optimiser vos dépenses sur ?"
 *
 * Règle de déclenchement stricte (page Accueil) :
 *   ❌ JAMAIS à l'arrivée sur la page d'accueil
 *   ❌ JAMAIS pendant le scroll de la page d'accueil
 *   ✅ UNIQUEMENT si l'utilisateur :
 *      • clique sur le menu (event 'optina:menu-opened' dispatché par Navbar), OU
 *      • quitte la page d'accueil pour naviguer vers une autre page du site
 *
 * Format : bandeau bas, jamais fenêtre centrale, croix de fermeture visible.
 * 2 boutons : Assurance → /assurance · Énergie → /energie
 * Une seule fois par session.
 */
const STORAGE_KEY = "optina_qualif_seen";

export default function QualificationPopup() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const triggered = useRef(false);
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") {
        triggered.current = true;
      }
    } catch {
      // ignore
    }
  }, []);

  // Trigger 1 : menu hamburger ouvert (event custom du Navbar)
  useEffect(() => {
    const onMenu = () => {
      if (triggered.current) return;
      triggered.current = true;
      setOpen(true);
    };
    window.addEventListener("optina:menu-opened", onMenu);
    return () => window.removeEventListener("optina:menu-opened", onMenu);
  }, []);

  // Trigger 2 : navigation depuis l'accueil "/" vers une autre page interne
  useEffect(() => {
    const prev = prevPath.current;
    const curr = location.pathname;
    if (prev === "/" && curr !== "/" && !triggered.current) {
      triggered.current = true;
      setOpen(true);
    }
    prevPath.current = curr;
  }, [location.pathname]);

  const close = () => {
    setClosing(true);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignore
    }
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 280);
  };

  const choose = (path) => {
    close();
    setTimeout(() => navigate(path), 280);
  };

  if (!open) return null;

  return (
    <div
      data-testid="qualif-popup"
      className={`fixed bottom-0 left-0 right-0 z-[55] pointer-events-none transition-transform duration-300 ${
        closing ? "translate-y-full" : "translate-y-0"
      }`}
    >
      <div className="mx-auto max-w-3xl px-4 pb-4 sm:pb-6">
        <div className="pointer-events-auto relative rounded-2xl bg-[#1A1A1A] border border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.4)] px-5 py-4 sm:px-6 sm:py-5">
          <button
            data-testid="qualif-popup-close"
            onClick={close}
            aria-label="Fermer"
            className="absolute top-3 right-3 h-8 w-8 rounded-full text-white/55 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>

          <div className="text-center sm:text-left sm:flex sm:items-center sm:justify-between sm:gap-6">
            <h3
              data-testid="qualif-popup-title"
              className="font-serif text-white text-[18px] md:text-[20px] leading-snug tracking-tight pr-8 sm:pr-0"
            >
              Vous souhaitez optimiser vos dépenses sur{" "}
              <span className="italic font-editorial">?</span>
            </h3>

            <div className="mt-4 sm:mt-0 grid grid-cols-2 sm:flex sm:items-center gap-2.5 sm:gap-3 shrink-0">
              <button
                data-testid="qualif-popup-assurance"
                onClick={() => choose("/assurance")}
                className="btn-press inline-flex items-center justify-center gap-2 rounded-full border border-white/40 hover:border-white hover:bg-white hover:text-[#1A1A1A] text-white px-5 py-2.5 text-[11.5px] font-semibold tracking-[0.22em] uppercase transition-colors"
              >
                <Shield className="h-3.5 w-3.5" />
                Assurance
              </button>
              <button
                data-testid="qualif-popup-energie"
                onClick={() => choose("/energie")}
                className="btn-press inline-flex items-center justify-center gap-2 rounded-full border border-white/40 hover:border-white hover:bg-white hover:text-[#1A1A1A] text-white px-5 py-2.5 text-[11.5px] font-semibold tracking-[0.22em] uppercase transition-colors"
              >
                <Zap className="h-3.5 w-3.5" />
                Énergie
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
